const API_BASE = process.env.REACT_APP_API_URL || '';

function connectJitterMs() {
  return 3000 + Math.floor(Math.random() * 12000);
}

export function connectRealtime({
  app,
  getToken,
  onInvalidate,
  onReconnect,
  debounceMs = 400,
} = {}) {
  let closed = false;
  let es = null;
  let retryMs = 15000;
  let debounceTimer = null;
  let startTimer = null;
  let retryTimer = null;
  const pending = new Set();
  let reconnectHookPending = false;

  function flush() {
    const domains = [...pending];
    pending.clear();
    if (!domains.length) return;
    try {
      window.dispatchEvent(new CustomEvent('oh:invalidate', { detail: { domains, app } }));
    } catch {
      /* ignore */
    }
    onInvalidate?.(domains);
  }

  function queueDomains(domains) {
    (domains || []).forEach((d) => pending.add(d));
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(flush, debounceMs);
  }

  function connect() {
    if (closed) return;
    try {
      es?.close();
    } catch {
      /* ignore */
    }
    const params = new URLSearchParams();
    if (app) params.set('app', app);
    const token = typeof getToken === 'function' ? getToken() : '';
    if (token) params.set('token', token);
    const url = `${API_BASE}/api/events?${params.toString()}`;
    es = new EventSource(url);

    es.addEventListener('ready', () => {
      retryMs = 15000;
      if (reconnectHookPending) {
        reconnectHookPending = false;
        onReconnect?.();
      }
    });

    es.addEventListener('invalidate', (ev) => {
      try {
        const data = JSON.parse(ev.data || '{}');
        queueDomains(data.domains || []);
      } catch {
        /* ignore */
      }
    });

    es.onerror = () => {
      try {
        es.close();
      } catch {
        /* ignore */
      }
      if (closed) return;
      reconnectHookPending = true;
      const wait = retryMs + Math.floor(Math.random() * 5000);
      retryMs = Math.min(Math.round(retryMs * 1.7), 180000);
      clearTimeout(retryTimer);
      retryTimer = setTimeout(connect, wait);
    };
  }

  function forceReconnect() {
    if (closed) return;
    retryMs = Math.max(retryMs, 15000);
    reconnectHookPending = true;
    clearTimeout(retryTimer);
    connect();
  }

  startTimer = setTimeout(connect, connectJitterMs());

  const onOnline = () => forceReconnect();
  const onVis = () => {
    if (document.visibilityState !== 'visible') return;
    if (!es || es.readyState !== EventSource.OPEN) forceReconnect();
  };
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVis);

  return () => {
    closed = true;
    clearTimeout(debounceTimer);
    clearTimeout(startTimer);
    clearTimeout(retryTimer);
    try {
      es?.close();
    } catch {
      /* ignore */
    }
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVis);
  };
}

export function bootStaggerMs(maxMs = 8000) {
  return Math.floor(Math.random() * Math.max(0, maxMs));
}
